import type { Dispatch, SetStateAction } from "react"
import { createContext, useContext, useMemo, useRef, useState } from "react"
import { Box, LinearProgress, Stack, Typography } from "@mui/material"
import { alpha, darken } from "@mui/material/styles"
import { noop, isArray, find, filter } from "lodash"

import type { FC, FCWC, ResponsiveImageType } from ".."
import {
  Button,
  ContainerGrid,
  IconButton,
  InquiryModal,
  Link,
  ResponsiveImage,
  SvgIcon,
  WYSIWYG,
} from ".."

type ContactCTAProps = {
  subtitle: string
  title: string
  description: string
  image?: ResponsiveImageType
  link: {
    title: string
    uri: string
    uri_alias?: string
  }
  staff: {
    name: string
    location: string
    bio: string
    image: ResponsiveImageType
    uri: string
  }[]
}

const ContactCTA: FC<ContactCTAProps> = ({ title, subtitle, description, staff, image, link }) => {
  return (
    <ContainerGrid pt={{ xs: 5, md: 10 }} pb={{ xs: 5, md: 10 }} overflow={{ md: "hidden" }}>
      <ContactCTAContent title={title} subtitle={subtitle} description={description} link={link} />
      <ContactCTAStaff staff={staff} image={image} />
    </ContainerGrid>
  )
}
export default ContactCTA

/** A sub-component for the left content description in the Contact CTA */
type ContentProps = Pick<ContactCTAProps, "title" | "subtitle" | "description" | "link">
const ContactCTAContent: FC<ContentProps> = ({ title, subtitle, description, link }) => {
  const [openInquiryModal, setOpenInquiryModal] = useState(false)

  return (
    <>
      <Stack
        justifyContent="center"
        minHeight={{ xs: 480, lg: 800 }}
        gridColumn={{ xs: "1 / -1", md: "auto / span 4", lg: "auto / span 6" }}
        color={(theme) => theme.palette["neutral.700"].main}
        bgcolor={(theme) => theme.palette["tertiary.300"].main}
        px={{ xs: 5, md: 10, lg: 15, xl: 25 }}
        py={{ xs: 15, md: 25, lg: 15, xl: 25 }}
        borderRadius={1.5}
        overflow="hidden"
      >
        <Typography component="h3" variant="body.lg" mb={{ xs: 5, md: 10 }}>
          {subtitle}
        </Typography>

        <Typography component="h2" variant="heading.xl" mb={5}>
          {title}
        </Typography>

        <Box mb={15}>
          <Typography variant="body.lg">
            <Box dangerouslySetInnerHTML={{ __html: description }} />
          </Typography>
        </Box>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", lg: "row" },
            gap: { xs: 2, lg: 3 },
            width: "100%",
          }}
        >
          <Button variant="FullColorOrangeBtn" onClick={() => setOpenInquiryModal(true)}
            sx={{ width: { xs: "100%", lg: link?.title ? "50%" : "85%" } }}>
            Request a custom quote
          </Button>
          {link?.title && (
            <Link
              type="base"
              variant="link.sm"
              href={link.uri_alias || link.uri}
              sx={(theme) => ({
                "alignItems": "center",
                "bgcolor": theme.palette["neutral.100"].main,
                "boxShadow": `1px 1px 2px ${theme.palette["tertiary.800"].main}`,
                "borderRadius": 1,
                "color": theme.palette["neutral.700"].main,
                "display": "inline-flex",
                "lineHeight": 1,
                "justifyContent": "center",
                "transition": theme.transitions.create([
                  "background-color",
                  "border-color",
                  "box-shadow",
                ]),
                "minHeight": { xs: 54, md: 62 },
                "px": { xs: 7.5, md: 10 },
                "py": 3.5,
                "width": { xs: "100%", lg: "50%" },

                "&:hover, &:focus-visible": {
                  bgcolor: theme.palette["tertiary.800"].main,
                  boxShadow: `1px 1px 2px ${theme.palette["tertiary.900"].main}`,
                },
              })}
            >
              {link?.title && link.title.toLowerCase().includes("video call") && (
                <SvgIcon variant="videoChat" size={20} sx={{ mr: 3 }} />
              )}
              <span>{link.title}</span>
            </Link>
          )}
        </Box>

      </Stack>
      <InquiryModal open={openInquiryModal} onClose={() => setOpenInquiryModal(false)} />
    </>
  )
}

/** A sub-component for the right content for staff information in the Contact CTA */
type StaffProps = Pick<ContactCTAProps, "image" | "staff">
const ContactCTAStaff: FC<StaffProps> = ({ image, staff }) => {
  const xlImageVariant = find(image?.variants, { style: "3xl" })
  const defaultImageVariant = xlImageVariant && {
    ...xlImageVariant,
    style: "default",
  }

  const overrideImageVariants = {
    variants: filter([xlImageVariant, defaultImageVariant]),
  } as ResponsiveImageType

  const defaultImage: ResponsiveImageType = useMemo(() => {
    return {
      variants: [
        {
          alt: "GoWay Travel Destination Expert Hero Background",
          height: 2500,
          style: "default",
          title: "",
          url: "/images/goway-travel-destination-expert-hero-background.webp",
          width: 2300,
        },
        {
          alt: "GoWay Travel Destination Expert Hero Background",
          height: null,
          style: "sm",
          title: "",
          url: "/images/goway-travel-destination-expert-hero-background-sm.webp",
          width: null,
        },
        {
          alt: "GoWay Travel Destination Expert Hero Background",
          height: null,
          style: "md",
          title: "",
          url: "/images/goway-travel-destination-expert-hero-background-md.webp",
          width: null,
        },
        {
          alt: "GoWay Travel Destination Expert Hero Background",
          height: null,
          style: "lg",
          title: "",
          url: "/images/goway-travel-destination-expert-hero-background-lg.webp",
          width: null,
        },
        {
          alt: "GoWay Travel Destination Expert Hero Background",
          height: null,
          style: "xl",
          title: "",
          url: "/images/goway-travel-destination-expert-hero-background-xl.webp",
          width: null,
        },
        {
          alt: "GoWay Travel Destination Expert Hero Background",
          height: null,
          style: "2xl",
          title: "",
          url: "/images/goway-travel-destination-expert-hero-background-2xl.webp",
          width: null,
        },
        {
          alt: "GoWay Travel Destination Expert Hero Background",
          height: null,
          style: "3xl",
          title: "",
          url: "/images/goway-travel-destination-expert-hero-background-3xl.webp",
          width: null,
        },
      ],
    }
  }, [])

  return (
    <Box
      height="100%"
      pt={{ xs: 12.5, md: 30 }}
      pb={{ xs: 25, md: 35 }}
      pl={5}
      pr={10}
      bgcolor={(theme) => theme.palette["neutral.300"].main}
      borderRadius={1.5}
      overflow="hidden"
      gridColumn={{ xs: "1 / -1", md: "auto / span 4", lg: "auto / span 6" }}
      position="relative"
      sx={{
        display: { xs: "none", md: "block" }, // hide on mobile, show on md and up
      }}
    >
      <ResponsiveImage
        image={overrideImageVariants.variants.length > 0 ? overrideImageVariants : defaultImage}
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          bottom: 0,
          right: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          pointerEvents: "none",
        }}
      />
      {isArray(staff) && <StaffCarousel staff={staff} />}
    </Box>
  )
}

let staffId = 0
const StaffCarouselContext = createContext<{
  active: [index: number, setIndex: Dispatch<SetStateAction<number>>]
  numSlides: number
}>({
  active: [0, noop],
  numSlides: 0,
})

/** A sub-component for the carousel of staff cards in the Contact CTA */
type StaffCarouselProps = Pick<ContactCTAProps, "staff">
const StaffCarousel: FC<StaffCarouselProps> = ({ staff }) => {
  const numSlides = staff.length
  const [index, setIndex] = useState(0)
  staffId = 0

  return (
    <StaffCarouselContext.Provider value={{ active: [index, setIndex], numSlides }}>
      <Stack height={1} position="relative" justifyContent="center" alignItems="center">
        <Box position="relative" sx={{ isolation: "isolate" }}>
          {staff.map(({ name, location, image, bio, uri }, i) => (
            <StaffCard key={i}>
              <StaffCardHeader name={name} location={location} image={image} />
              <StaffCardContent name={name} bio={bio} uri={uri} />
            </StaffCard>
          ))}
        </Box>
      </Stack>
      <StaffCarouselProgress />
    </StaffCarouselContext.Provider>
  )
}

/** A sub-component for the individual staff card in the Contact CTA */
const StaffCard: FCWC = ({ children }) => {
  const {
    active: [activeIndex, setActiveIndex],
    numSlides,
  } = useContext(StaffCarouselContext)
  const { current: slideIndex } = useRef(staffId++)
  const slideOffset = (((slideIndex - activeIndex) % numSlides) + numSlides) % numSlides
  const isActive = slideIndex === activeIndex

  return (
    <Box
      borderRadius={1.5}
      width={1}
      maxWidth={387}
      zIndex={Math.abs(numSlides - slideOffset)}
      p={{ xs: 5, md: 7.5 }}
      sx={{
        position: isActive ? "relative" : "absolute",
        top: 0,
        left: "50%",
        bgcolor: (theme) =>
          darken(
            theme.palette[isActive ? "neutral.100" : "secondary.200"].main,
            Math.max(Math.min(0.05 * (slideOffset - 1), 1), 0)
          ),
        transform: `translate(calc(-50% + ${10 * slideOffset - 1}px), ${10 * slideOffset - 1}px)`,
        transformOrigin: "bottom center",
        transition: (theme) =>
          theme.transitions.create("all", {
            duration: theme.transitions.duration.standard * 2,
            easing: "cubic-bezier(0.18, 0.98, 0.45, 1)",
          }),
        opacity: slideOffset > 2 ? 0 : 1,
        pointerEvents: isActive ? "auto" : "none",
        overflow: "hidden",
      }}
    >
      <Stack spacing={{ xs: 5, md: 7.5 }}>{children}</Stack>
    </Box>
  )
}

/** A sub-component for the header in the staff card in the Contact CTA */
type HeaderProps = {
  name: string
  location: string
  image: ResponsiveImageType
}
const StaffCardHeader: FC<HeaderProps> = ({ name, location, image }) => {
  // util to update the default style of the image
  const updateDefaultStyle = (image: ResponsiveImageType) => {
    if (!image.variants) {
      return image
    }

    const filteredVariants = image.variants.filter((variant) => variant.style !== "default")
    const updatedVariants = filteredVariants.map((variant) => {
      if (variant.style === "small") {
        return { ...variant, style: "default" }
      }
      return variant
    })

    return {
      variants: updatedVariants,
    }
  }

  return (
    <Stack
      direction="row"
      spacing={1.5}
      justifyContent="space-between"
      alignItems="center"
      pb={{ xs: 5, md: 7.5 }}
      borderBottom={(theme) => `1px solid ${theme.palette["neutral.200"].main}`}
    >
      <Box>
        <Typography variant="heading.sm" component="div">
          {name}
        </Typography>
        <Typography
          variant="body.sm"
          component="div"
          sx={{
            "overflow": "hidden",
            "display": "-webkit-box",
            "WebkitBoxOrient": "vertical",
            "WebkitLineClamp": 2,

            "& > *": {
              display: "inline",
            },
          }}
        >
          {location}
        </Typography>
      </Box>
      <Box
        sx={{
          width: { xs: 60, md: 80 },
          height: { xs: 60, md: 80 },
          bgcolor: (theme) => theme.palette["secondary.200"].main,
          borderRadius: 9999,
          overflow: "hidden",
        }}
      >
        {image && <ResponsiveImage image={updateDefaultStyle(image) as ResponsiveImageType} />}
      </Box>
    </Stack>
  )
}

/** A sub-component for the content in the staff card in the Contact CTA */
type CardContentProps = {
  name: string
  bio: string
  uri: string
}
const StaffCardContent: FC<CardContentProps> = ({ name, bio, uri }) => {
  const {
    active: [_, setIndex],
    numSlides,
  } = useContext(StaffCarouselContext)

  return (
    <>
      <WYSIWYG
        sx={{
          "overflow": "hidden",
          "display": "-webkit-box",
          "WebkitBoxOrient": "vertical",
          "WebkitLineClamp": 4,
          "minHeight": "112px",

          "& > *": {
            display: "inline",
          },
        }}
        content={bio}
      />
      <Box>
        <Link href={uri} aria-label={`Read more about ${name}`}>
          Read More
        </Link>
      </Box>
      <Box textAlign="right">
        <IconButton
          variant="secondary"
          aria-label={`See next person`}
          icon={<SvgIcon variant="arrowOutward" size={{ xs: 13, md: 16 }} />}
          onClick={() => setIndex((index) => (index + 1 > numSlides - 1 ? 0 : index + 1))}
        />
      </Box>
    </>
  )
}

/** A sub-component for the progress bar of the carousel in the Contact CTA */
const StaffCarouselProgress: FC = () => {
  const {
    active: [index],
    numSlides,
  } = useContext(StaffCarouselContext)

  return (
    <Box
      position="absolute"
      zIndex={5}
      bottom={40}
      left="50%"
      width={1}
      maxWidth={80}
      sx={{ transform: "translateX(-50%)" }}
    >
      <LinearProgress
        variant="determinate"
        aria-label="staff profiles progress bar"
        value={((index + 1) / numSlides) * 100}
        sx={{
          "height": "2px",
          "bgcolor": (theme) => alpha(theme.palette["neutral.100"].main, 0.4),

          "& > .MuiLinearProgress-bar1Determinate": {
            bgcolor: (theme) => theme.palette["neutral.100"].main,
          },
        }}
      />
    </Box>
  )
}
